import React, { useEffect, useState, useMemo } from 'react';
import {
  Grid,
  Typography,
  Button,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Paper
} from '@material-ui/core/';
import { Form, Field } from 'react-final-form';
import { fundBillingStatus } from './FundLookups';
import FundBillingApi from '../../core/apis/FundBillingApi';
import { TableSortLabel } from '@material-ui/core';
import { SelectInput } from 'react-admin';

const FundBillingTab = ({ fundId }) => {
  const [initialValues, setInitialValues] = useState({});
  const [billingHistory, setBillingHistory] = useState([]);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

  const sortedHistory = useMemo(() => {
    if (!sortConfig.key) return billingHistory;

    const sorted = [...billingHistory].sort((a, b) => {
      const aValue = a[sortConfig.key];
      const bValue = b[sortConfig.key];

      if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });

    return sorted;
  }, [billingHistory, sortConfig]);

  const handleSort = key => {
    setSortConfig(prev => {
      if (prev.key === key) {
        return { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
      }
      return { key, direction: 'asc' };
    });
  };

  const formatOperationType = type => {
    switch (type) {
      case 'C':
        return 'Created';
      case 'U':
        return 'Updated';
      case 'D':
        return 'Deleted';
      default:
        return type;
    }
  };

  useEffect(() => {
    const loadBilling = async () => {
      try {
        const billingData = await FundBillingApi.fetchFundBilling(fundId);
        const historyData = await FundBillingApi.fetchFundBillingHistory(
          fundId
        );

        setInitialValues({ billedStatus: billingData.billedStatus });
        setBillingHistory(historyData || []);
      } catch (error) {
        console.error('Error fetching fund billing:', error);
      }
    };

    loadBilling();
  }, [fundId]);

  const onSubmit = async values => {
    try {
      const payload = { fundId, ...values };
      const updated = await FundBillingApi.createOrUpdateFundBilling(payload);

      setInitialValues({ billedStatus: updated.billedStatus });

      // Refresh history after update
      const updatedHistory = await FundBillingApi.fetchFundBillingHistory(
        fundId
      );
      setBillingHistory(updatedHistory || []);
    } catch (error) {
      console.error('Error updating fund billing:', error);
    }
  };

  const formatFundBillingStatusValue = value => {
    if (!value) return '/';
    const match = fundBillingStatus.find(status => status.id === value);
    return match ? match.name : value;
  };

  return (
    <>
      <Typography variant="h6" gutterBottom>
        Fund Billing
      </Typography>

      <Form onSubmit={onSubmit} initialValues={initialValues}>
        {({ handleSubmit, pristine }) => (
          <form onSubmit={handleSubmit}>
            <Grid container spacing={2}>
              <Grid item xs={4}>
                <Field name="billedStatus">
                  {({ input, meta }) => (
                    <SelectInput
                      source="billedStatus"
                      label="Billed Status"
                      choices={fundBillingStatus}
                      validate={[]}
                      variant="standard"
                      {...input}
                    />
                  )}
                </Field>
              </Grid>

              <Grid item xs={12}>
                <Button
                  variant="contained"
                  color="primary"
                  type="submit"
                  disabled={pristine}
                >
                  Save
                </Button>
              </Grid>

              <Grid item xs={12}>
                <Divider />
              </Grid>

              <Grid item xs={12}>
                <Typography variant="subtitle1" gutterBottom>
                  Fund Billing History
                </Typography>
                <Paper>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell
                          sortDirection={
                            sortConfig.key === 'fundId'
                              ? sortConfig.direction
                              : false
                          }
                        >
                          <TableSortLabel
                            active={sortConfig.key === 'fundId'}
                            direction={sortConfig.direction}
                            onClick={() => handleSort('fundId')}
                          >
                            Fund ID
                          </TableSortLabel>
                        </TableCell>

                        <TableCell
                          sortDirection={
                            sortConfig.key === 'operationDate'
                              ? sortConfig.direction
                              : false
                          }
                        >
                          <TableSortLabel
                            active={sortConfig.key === 'operationDate'}
                            direction={sortConfig.direction}
                            onClick={() => handleSort('operationDate')}
                          >
                            Operation Date
                          </TableSortLabel>
                        </TableCell>

                        <TableCell
                          sortDirection={
                            sortConfig.key === 'operationAuthorId'
                              ? sortConfig.direction
                              : false
                          }
                        >
                          <TableSortLabel
                            active={sortConfig.key === 'operationAuthorId'}
                            direction={sortConfig.direction}
                            onClick={() => handleSort('operationAuthorId')}
                          >
                            Author
                          </TableSortLabel>
                        </TableCell>

                        <TableCell
                          sortDirection={
                            sortConfig.key === 'operationType'
                              ? sortConfig.direction
                              : false
                          }
                        >
                          <TableSortLabel
                            active={sortConfig.key === 'operationType'}
                            direction={sortConfig.direction}
                            onClick={() => handleSort('operationType')}
                          >
                            Operation Type
                          </TableSortLabel>
                        </TableCell>

                        <TableCell
                          sortDirection={
                            sortConfig.key === 'oldValue'
                              ? sortConfig.direction
                              : false
                          }
                        >
                          <TableSortLabel
                            active={sortConfig.key === 'oldValue'}
                            direction={sortConfig.direction}
                            onClick={() => handleSort('oldValue')}
                          >
                            Old Value
                          </TableSortLabel>
                        </TableCell>

                        <TableCell
                          sortDirection={
                            sortConfig.key === 'newValue'
                              ? sortConfig.direction
                              : false
                          }
                        >
                          <TableSortLabel
                            active={sortConfig.key === 'newValue'}
                            direction={sortConfig.direction}
                            onClick={() => handleSort('newValue')}
                          >
                            New Value
                          </TableSortLabel>
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {sortedHistory.map(entry => (
                        <TableRow key={entry.operationDate}>
                          <TableCell>{entry.fundId}</TableCell>
                          <TableCell>
                            {new Date(entry.operationDate).toLocaleString()}
                          </TableCell>
                          <TableCell>
                            {entry.operationAuthorId || '/'}
                          </TableCell>
                          <TableCell>
                            {formatOperationType(entry.operationType)}
                          </TableCell>
                          <TableCell>
                            {formatFundBillingStatusValue(entry.oldValue)}
                          </TableCell>
                          <TableCell>
                            {formatFundBillingStatusValue(entry.newValue)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Paper>
              </Grid>
            </Grid>
          </form>
        )}
      </Form>
    </>
  );
};

export default FundBillingTab;
